import { Button } from '@/components/Atoms/Button/Button';
import { Loading, Size } from '@/components/Atoms/Loading/Loading';
import { ModalForm } from '@/components/Atoms/ModalForm/ModalForm';
import { Notification } from '@/components/Atoms/Notification/Notification';
import { P } from '@/components/Atoms/P/P';
import { CsrfForm } from '@/components/Molecules/CsrfForm/CsrfForm';
import type { IDialogControl } from '@/components/Molecules/Dialog/Dialog';
import { Dialog, DialogActions, DialogActionsMain, DialogActionsSecondary } from '@/components/Molecules/Dialog/Dialog';
import useTranslation from '@/hooks/useTranslation';
import { trackEvent } from '@/services/Analytics/BBAnalytics';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import type { APIClientError } from '@/services/Http/ApiClientError';
import type { ComponentProps, FC } from 'react';
import { useState } from 'react';

interface IProps {
    dialog: IDialogControl;
    form: ComponentProps<typeof CsrfForm>;
}

export const EmailModal: FC<IProps> = ({ dialog, form }) => {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [failed, setFailed] = useState<boolean>(false);
    const [failMessageKey, setFailMessageKey] = useState<string>('');

    const closeModal = () => {
        setIsSubmitting(false);
    };

    const onSubmitStart = () => {
        setFailed(false);
        setSuccess(false);
        setIsSubmitting(true);
    };

    const onSubmitEnd = () => {
        setSuccess(true);
        setIsSubmitting(false);
        trackEvent(TrackingAction.Submit, TrackingCategory.Share, 'Share via Email');
    };

    const onSubmitError = (e: APIClientError) => {
        if (e?.response?.status === 403) {
            setFailMessageKey('failDenied');
        } else if (e?.response?.status === 429) {
            setFailMessageKey('failRateLimited');
        } else if (e?.response?.status === 400 && e.message.includes('Invalid email address')) {
            setFailMessageKey('failDenied');
        } else {
            console.warn(`[EmailModal Error] ${e.message}`);
            setFailMessageKey('');
        }
        setFailed(true);
        setIsSubmitting(false);
    };

    return (
        <Dialog title={t('common:Modals.EmailModal.title')} {...dialog} onClose={closeModal}>
            <P>{t('common:Modals.EmailModal.body')}</P>
            <CsrfForm {...form} onSubmitStart={onSubmitStart} onSubmitEnd={onSubmitEnd} onSubmitError={onSubmitError}>
                <ModalForm>
                    <label>
                        {t('common:Modals.EmailModal.to')}
                        <input required name="email" type="email" disabled={isSubmitting} autoFocus />
                    </label>
                    <label>
                        {t('common:Modals.EmailModal.message')}
                        <textarea name="message" required disabled={isSubmitting} />
                    </label>
                    <DialogActions>
                        <DialogActionsMain>
                            <Button type="submit" disabled={isSubmitting}>
                                {t('common:Modals.EmailModal.send')}
                            </Button>
                        </DialogActionsMain>
                        <DialogActionsSecondary>
                            {failed && (
                                <Notification state="error">
                                    {t('common:Modals.EmailModal.' + (failMessageKey || 'fail'))}
                                </Notification>
                            )}
                            {success && (
                                <Notification state="success">{t('common:Modals.EmailModal.success')}</Notification>
                            )}
                            {isSubmitting && <Loading size={Size.Small} />}
                        </DialogActionsSecondary>
                    </DialogActions>
                </ModalForm>
            </CsrfForm>
        </Dialog>
    );
};
